// Hook for Epic Pushback functionality
// Manages settings, templates, and logs

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  EpicIntegrationSettings, 
  EpicPushbackTemplate, 
  EpicPushbackLog,
  EpicPushbackEventType,
  EpicPushbackDestination,
} from '@/lib/epic/pushbackTypes';

// Fetch all Epic integration settings
export function useEpicSettings() {
  return useQuery({
    queryKey: ['epic-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('epic_integration_settings')
        .select('*')
        .order('client_name');
      
      if (error) throw error;
      return data as EpicIntegrationSettings[];
    },
  });
}

// Fetch settings for a specific client
export function useEpicClientSettings(clientId: string | null) {
  return useQuery({
    queryKey: ['epic-settings', clientId],
    queryFn: async () => {
      if (!clientId) return null;
      
      const { data, error } = await supabase
        .from('epic_integration_settings')
        .select('*')
        .eq('client_id', clientId)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data as EpicIntegrationSettings | null;
    },
    enabled: !!clientId,
  });
}

// Update Epic settings
export function useUpdateEpicSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: Partial<EpicIntegrationSettings> & { client_id: string; client_name: string }) => {
      const { data, error } = await supabase
        .from('epic_integration_settings')
        .upsert({
          client_id: settings.client_id,
          client_name: settings.client_name,
          environment: settings.environment || 'sandbox',
          epic_enabled: settings.epic_enabled ?? false,
          fhir_base_url: settings.fhir_base_url,
          oauth_client_id: settings.oauth_client_id,
          oauth_redirect_uri: settings.oauth_redirect_uri,
          pushback_enabled: settings.pushback_enabled ?? false,
          pushback_inbasket: settings.pushback_inbasket ?? true,
          pushback_task: settings.pushback_task ?? true,
          pushback_chart_note: settings.pushback_chart_note ?? false,
          default_pushback_destination: settings.default_pushback_destination || 'task',
          pushback_on_case_created: settings.pushback_on_case_created ?? false,
          pushback_on_submitted: settings.pushback_on_submitted ?? true,
          pushback_on_more_info: settings.pushback_on_more_info ?? true,
          pushback_on_approved: settings.pushback_on_approved ?? true,
          pushback_on_denied: settings.pushback_on_denied ?? true,
          pushback_on_appealed: settings.pushback_on_appealed ?? true,
          pushback_on_closed: settings.pushback_on_closed ?? false,
        }, { onConflict: 'client_id' })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epic-settings'] });
      toast.success('Epic settings saved');
    },
    onError: (error) => {
      toast.error(`Failed to save settings: ${error.message}`);
    },
  });
}

// Fetch pushback templates
export function useEpicTemplates(clientId?: string) {
  return useQuery({
    queryKey: ['epic-templates', clientId],
    queryFn: async () => {
      let query = supabase
        .from('epic_pushback_templates')
        .select('*')
        .eq('is_active', true)
        .order('event_type');
      
      if (clientId) {
        query = query.or(`client_id.eq.${clientId},client_id.eq.default`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as EpicPushbackTemplate[];
    },
  });
}

// Update or create a template
export function useUpdateEpicTemplate() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (template: { 
      client_id: string; 
      event_type: EpicPushbackEventType;
      template_name: string;
      subject_template: string;
      body_template: string;
    }) => {
      // Get current version if exists
      const { data: existing } = await supabase
        .from('epic_pushback_templates')
        .select('version')
        .eq('client_id', template.client_id)
        .eq('event_type', template.event_type)
        .eq('is_active', true)
        .single();
      
      // Deactivate old version if exists
      if (existing) {
        await supabase
          .from('epic_pushback_templates')
          .update({ is_active: false })
          .eq('client_id', template.client_id)
          .eq('event_type', template.event_type)
          .eq('is_active', true);
      }
      
      // Insert new version
      const { data, error } = await supabase
        .from('epic_pushback_templates')
        .insert({
          client_id: template.client_id,
          event_type: template.event_type,
          template_name: template.template_name,
          subject_template: template.subject_template,
          body_template: template.body_template,
          version: (existing?.version || 0) + 1,
          is_active: true,
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epic-templates'] });
      toast.success('Template saved');
    },
    onError: (error) => {
      toast.error(`Failed to save template: ${error.message}`);
    },
  });
}

// Fetch pushback logs for an authorization
export function useEpicPushbackLogs(authorizationId: string | null) {
  return useQuery({
    queryKey: ['epic-pushback-logs', authorizationId],
    queryFn: async () => {
      if (!authorizationId) return [];
      
      const { data, error } = await supabase
        .from('epic_pushback_logs')
        .select('*')
        .eq('authorization_id', authorizationId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as EpicPushbackLog[];
    },
    enabled: !!authorizationId,
  });
}

// Create a pushback log entry
export function useCreatePushbackLog() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (log: {
      authorization_id: string;
      client_id: string;
      event_type: string;
      destination: string;
      status: string;
      request_payload?: Record<string, unknown>;
      is_manual_resend: boolean;
      triggered_by?: string;
    }) => {
      const { data, error } = await supabase
        .from('epic_pushback_logs')
        .insert({
          authorization_id: log.authorization_id,
          client_id: log.client_id,
          event_type: log.event_type,
          destination: log.destination,
          status: log.status,
          request_payload: log.request_payload as unknown as Record<string, never>,
          is_manual_resend: log.is_manual_resend,
          triggered_by: log.triggered_by,
          attempt_count: 1,
          last_attempt_at: new Date().toISOString(),
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['epic-pushback-logs', data.authorization_id] });
    },
  });
}

// Update pushback log status
export function useUpdatePushbackLog() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      status, 
      errorMessage,
      responsePayload,
      fhirResourceId,
    }: { 
      id: string; 
      status: EpicPushbackLog['status']; 
      errorMessage?: string;
      responsePayload?: Record<string, unknown>;
      fhirResourceId?: string;
    }) => {
      const { data, error } = await supabase
        .from('epic_pushback_logs')
        .update({
          status,
          error_message: errorMessage,
          response_payload: responsePayload as unknown as Record<string, never>,
          fhir_resource_id: fhirResourceId,
          last_attempt_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['epic-pushback-logs', data.authorization_id] });
    },
  });
}

// Manual resend of a pushback
export function useResendPushback() {
  const queryClient = useQueryClient();
  const createLog = useCreatePushbackLog();
  
  return useMutation({
    mutationFn: async (originalLog: EpicPushbackLog) => {
      // Create a new log entry for the resend
      const newLog = await createLog.mutateAsync({
        authorization_id: originalLog.authorization_id,
        client_id: originalLog.client_id,
        event_type: originalLog.event_type as EpicPushbackEventType,
        destination: originalLog.destination as EpicPushbackDestination,
        status: 'PENDING',
        request_payload: originalLog.request_payload,
        is_manual_resend: true,
        triggered_by: 'manual_resend',
      });
      
      // In a real implementation, this would trigger the actual FHIR API call
      // For now, we simulate the send
      toast.info('Pushback queued for resend');
      
      return newLog;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['epic-pushback-logs', data.authorization_id] });
      toast.success('Pushback resend initiated');
    },
    onError: (error) => {
      toast.error(`Failed to resend: ${error.message}`);
    },
  });
}

// Hook to trigger a pushback event
export function useTriggerPushback() {
  const createLog = useCreatePushbackLog();
  
  return useCallback(async (
    authorizationId: string,
    clientId: string,
    eventType: EpicPushbackEventType,
    destination: EpicPushbackDestination,
    requestPayload: Record<string, unknown>
  ) => {
    try {
      // Create log entry
      const log = await createLog.mutateAsync({
        authorization_id: authorizationId,
        client_id: clientId,
        event_type: eventType,
        destination: destination,
        status: 'PENDING',
        request_payload: requestPayload as Record<string, never>,
        is_manual_resend: false,
      });
      
      // In production, this would call the Epic FHIR API
      // For now, we simulate success after a delay
      console.log('Epic pushback triggered:', { eventType, destination, authorizationId });
      
      return log;
    } catch (error) {
      console.error('Failed to trigger pushback:', error);
      throw error;
    }
  }, [createLog]);
}
